import { FieldArray } from 'react-final-form-arrays'
import Button from '@material-ui/core/Button'
import FormRow from '../../FormRow'
import { PlusCircle } from 'mdi-material-ui'
import { client } from '../../../../client'
import { useField, useForm } from 'react-final-form'
import { dateTimeToDate } from '../../../../utils/dates'
import FormSection from '../../FormSection'
import NotesSection from '../NotesSection/NotesSection'
import IconButton from '@material-ui/core/IconButton'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogActions from '@material-ui/core/DialogActions'
import Dialog from '@material-ui/core/Dialog'
import Alert from '@material-ui/lab/Alert'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { memo, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const EditDateDialog = ({ date, open, onClose, onChange }) => {
  const { t } = useTranslation('sessionNotesSection')
  const [selectedDate, handleDateChange] = useState(date)

  const handleClose = () => {
    handleDateChange(date)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{t('changeDate')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Alert severity="info">{t('alertInfo')}</Alert>
        </DialogContentText>
        <KeyboardDatePicker
          value={selectedDate}
          onChange={(date) => handleDateChange(date)}
          format="dd-MM-yyyy"
          invalidDateMessage={t('invalidDateMessage')}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          {t('cancel')}
        </Button>
        <Button
          onClick={() => {
            onChange(selectedDate)
            onClose()
          }}
          color="primary"
          autoFocus
        >
          {t('confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// One session note. Memoised on primitives so that editing note 3 does not
// re-render the rich-text editors of every other note on the file. The
// FieldArray this sits in subscribes to the whole array's value, so its render
// function runs on every keystroke; without this boundary a 79-session file
// re-rendered 79 draft-js editors per character typed (SLAAP-320).
const SessionNote = memo(
  ({ fieldName, createdAt, index, isOpen, onOpen, onClose, onChangeDate, onRemove }) => {
    const { t } = useTranslation('sessionNotesSection')

    return (
      <FormSection
        anchor={`session${index + 1}`}
        title={
          <div>
            {`${t('notes')} `} {dateTimeToDate(createdAt)}{' '}
            <EditIcon style={{ fontSize: 14 }} onClick={() => onOpen(index)} />
            {/* Only the open dialog is mounted; there is no reason to keep one
                per note in the tree. */}
            {isOpen && (
              <EditDateDialog
                date={new Date(createdAt)}
                open
                onClose={onClose}
                onChange={(date) => onChangeDate(index, date)}
              />
            )}
          </div>
        }
        name={`${fieldName}.text`}
        component={NotesSection}
        action={
          <IconButton onClick={() => onRemove(index)}>
            <DeleteIcon color="disabled" fontSize="small" />
          </IconButton>
        }
      />
    )
  }
)

SessionNote.displayName = 'SessionNote'

const SessionNotesSection = () => {
  const { t } = useTranslation('sessionNotesSection')

  const {
    input: { value: programId }
  } = useField('id')

  const form = useForm()

  const [open, setOpen] = useState(-1)

  const handleAddSessionNote = async () => {
    return await client.addNote(programId)
  }

  // `fields` is a fresh object on every FieldArray render, so the handlers
  // below read it out of a ref instead of closing over it. That keeps their
  // identities stable, which is what lets SessionNote's memo hold.
  const fieldsRef = useRef(null)

  const handleOpen = useCallback((index) => setOpen(index), [])
  const handleClose = useCallback(() => setOpen(-1), [])

  const handleRemove = useCallback((index) => {
    const removed = fieldsRef.current.remove(index)
    if (removed) client.removeNote(removed.id)
  }, [])

  const handleChangeDate = useCallback(
    async (index, date) => {
      const note = {
        ...fieldsRef.current.value[index],
        createdAt: date.toISOString()
      }
      await client.updateNote(note.id, note)
      const sessionNotes = await client.findNotesByTreatmentProgramId(programId)
      form.mutators.setValue('sessionNotes', sessionNotes)
    },
    [form, programId]
  )

  return (
    <FieldArray name="sessionNotes">
      {({ fields }) => {
        fieldsRef.current = fields

        return (
          <>
            {fields.map((name, index) => (
              <SessionNote
                key={fields.value[index].id}
                fieldName={name}
                createdAt={fields.value[index].createdAt}
                index={index}
                isOpen={open === index}
                onOpen={handleOpen}
                onClose={handleClose}
                onChangeDate={handleChangeDate}
                onRemove={handleRemove}
              />
            ))}
            <FormRow>
              <Button
                fullWidth
                type="button"
                color="primary"
                onClick={async () => {
                  const note = await handleAddSessionNote()
                  fields.push(note)
                }}
                style={{ marginTop: -20, marginBottom: 20 }}
              >
                <PlusCircle />
                {t('addSession')}
              </Button>
            </FormRow>
          </>
        )
      }}
    </FieldArray>
  )
}

export default SessionNotesSection
